// Batch 3 of the Compare tab content (English and Italian).
// Same structure as grammarCompareData.js. Add the ideas here, and they appear in the app
// after the earlier ideas of the same level.

export const BATCH_3 = [
    {
        id: 'this-that',
        level: 'A1',
        title: { en: 'This and that', it: 'Questo e quello' },
        summary: {
            en: 'Pointing at things that are near or far.',
            it: 'Indicare cose vicine o lontane.',
        },
        forms: {
            english: [
                {
                    name: 'This / that / these / those',
                    formula: {
                        en: 'this and these for things near you, that and those for things far from you. They change only for singular and plural.',
                        it: 'this e these per le cose vicine a te, that e those per le cose lontane. Cambiano solo tra singolare e plurale.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Questo',
                    formula: {
                        en: 'Near the speaker. It matches gender and number: questo, questa, questi, queste.',
                        it: 'Vicino a chi parla. Concorda in genere e numero: questo, questa, questi, queste.',
                    },
                },
                {
                    name: 'Quello',
                    formula: {
                        en: 'Far from the speaker. Before a noun it changes like the article: il / quel, lo / quello, l\' / quell\', la / quella, i / quei, gli / quegli, le / quelle.',
                        it: 'Lontano da chi parla. Davanti a un nome cambia come l\'articolo: il / quel, lo / quello, l\' / quell\', la / quella, i / quei, gli / quegli, le / quelle.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'this book', tag: 'this' },
                italian: { text: 'questo libro', tag: 'questo' },
            },
            {
                english: { text: 'that book', tag: 'that' },
                italian: { text: 'quel libro', tag: 'quel (like il)' },
            },
            {
                english: { text: 'these houses', tag: 'these' },
                italian: { text: 'queste case', tag: 'queste' },
            },
            {
                english: { text: 'that student', tag: 'that' },
                italian: { text: 'quello studente', tag: 'quello (like lo)' },
            },
            {
                english: { text: 'those friends', tag: 'those' },
                italian: { text: 'quegli amici', tag: 'quegli (like gli)' },
            },
            {
                english: { text: 'What is this?', tag: 'this (alone)' },
                italian: { text: "Che cos'è questo?", tag: 'questo (alone)' },
            },
        ],
        notes: [
            {
                en: 'English changes only for number (this / these, that / those). Italian changes for gender and number too: questo, questa, questi, queste.',
                it: "L'inglese cambia solo per il numero (this / these, that / those). L'italiano cambia anche per il genere: questo, questa, questi, queste.",
            },
            {
                en: "Quello is the tricky one: before a noun it follows the pattern of the article. If you know il, lo, la, i, gli, le, you know quel, quello, quella, quei, quegli, quelle.",
                it: "Quello è il più difficile: davanti a un nome segue lo schema dell'articolo. Se conosci il, lo, la, i, gli, le, conosci quel, quello, quella, quei, quegli, quelle.",
            },
        ],
    },
    {
        id: 'prepositions',
        level: 'A1',
        title: { en: 'In, at, on (prepositions)', it: 'A, in, su, da (preposizioni)' },
        summary: {
            en: 'The small words for places and times.',
            it: 'Le piccole parole per luoghi e tempi.',
        },
        forms: {
            english: [
                {
                    name: 'In / at / on',
                    formula: {
                        en: 'in for cities, countries, months and years. at for points, like at home or at five o\'clock. on for days and surfaces.',
                        it: 'in per città, paesi, mesi e anni. at per i punti, come at home o at five o\'clock. on per i giorni e le superfici.',
                    },
                },
            ],
            italian: [
                {
                    name: 'A / in / su / da',
                    formula: {
                        en: 'a for cities and small places, in for countries and regions, su for surfaces, da for people and their places. They join the article: a + il = al, a + la = alla, su + il = sul, da + il = dal.',
                        it: 'a per le città e i luoghi piccoli, in per paesi e regioni, su per le superfici, da per le persone e i loro luoghi. Si uniscono all\'articolo: a + il = al, a + la = alla, su + il = sul, da + il = dal.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I live in Rome.', tag: 'in (city)' },
                italian: { text: 'Vivo a Roma.', tag: 'a (city)' },
            },
            {
                english: { text: 'I live in Italy.', tag: 'in (country)' },
                italian: { text: 'Vivo in Italia.', tag: 'in (country)' },
            },
            {
                english: { text: 'I am at home.', tag: 'at' },
                italian: { text: 'Sono a casa.', tag: 'a' },
            },
            {
                english: { text: 'The meeting is at five o\'clock.', tag: 'at (time)' },
                italian: { text: 'La riunione è alle cinque.', tag: 'a + le = alle' },
            },
            {
                english: { text: 'See you on Monday.', tag: 'on (day)' },
                italian: { text: 'Ci vediamo lunedì.', tag: 'No preposition' },
            },
            {
                english: { text: 'I am going to the doctor.', tag: 'to the doctor' },
                italian: { text: 'Vado dal medico.', tag: 'da + il = dal' },
            },
            {
                english: { text: 'The book is on the table.', tag: 'on (surface)' },
                italian: { text: 'Il libro è sul tavolo.', tag: 'su + il = sul' },
            },
        ],
        notes: [
            {
                en: 'Italian cities take a (a Roma), but countries take in (in Italia). English uses in for both.',
                it: "Le città vogliono a (a Roma), mentre i paesi vogliono in (in Italia). L'inglese usa in per entrambi.",
            },
            {
                en: "Italian prepositions join the article: 'alla banca' is a + la + banca. This is one of the first things that looks strange to English speakers, and it is always the same pattern.",
                it: "Le preposizioni italiane si uniscono all'articolo: 'alla banca' è a + la + banca. È una delle prime cose che sembrano strane a chi parla inglese, ed è sempre lo stesso schema.",
            },
            {
                en: "To go to someone's place or a professional's office, Italian uses da: 'Vado dal medico', 'Sono da Marco'.",
                it: "Per andare a casa di qualcuno o nello studio di un professionista, l'italiano usa da: 'Vado dal medico', 'Sono da Marco'.",
            },
        ],
    },
    {
        id: 'object-pronouns',
        level: 'A2',
        title: { en: 'Object pronouns (him, her, them)', it: 'Pronomi oggetto (lo, la, gli, le)' },
        summary: {
            en: 'Replacing the person or thing that receives the action.',
            it: 'Sostituire la persona o la cosa che riceve l\'azione.',
        },
        forms: {
            english: [
                {
                    name: 'me, you, him, her, it, us, them',
                    formula: {
                        en: 'Placed after the verb: I see her. I gave him the book. The same word is used whether you do something to someone or for someone.',
                        it: 'Si mette dopo il verbo: I see her. I gave him the book. Si usa la stessa parola sia per fare qualcosa a qualcuno sia per qualcuno.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Direct: mi, ti, lo, la, ci, vi, li, le',
                    formula: {
                        en: 'Placed before the conjugated verb: La vedo. After an infinitive or a command, attach it to the end: Voglio vederli. Chiamami!',
                        it: 'Si mette prima del verbo coniugato: La vedo. Dopo un infinito o un comando, si attacca alla fine: Voglio vederli. Chiamami!',
                    },
                },
                {
                    name: 'Indirect: mi, ti, gli, le, ci, vi, gli',
                    formula: {
                        en: 'For "to him / to her / to them": Gli ho dato il libro. Le parlo.',
                        it: 'Per "a lui / a lei / a loro": Gli ho dato il libro. Le parlo.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I see her.', tag: 'verb + her' },
                italian: { text: 'La vedo.', tag: 'la + verb (direct)' },
            },
            {
                english: { text: 'I am talking to her.', tag: 'to her' },
                italian: { text: 'Le parlo.', tag: 'le + verb (indirect)' },
            },
            {
                english: { text: 'I gave him the book.', tag: 'verb + him' },
                italian: { text: 'Gli ho dato il libro.', tag: 'gli + verb (indirect)' },
            },
            {
                english: { text: 'I want to see them.', tag: 'verb + them' },
                italian: { text: 'Voglio vederli.', tag: 'Attached to the infinitive' },
            },
            {
                english: { text: 'Call me!', tag: 'verb + me' },
                italian: { text: 'Chiamami!', tag: 'Attached to the command' },
            },
            {
                english: { text: 'I ate it (the pizza).', tag: 'verb + it' },
                italian: { text: "L'ho mangiata.", tag: "l' + avere + participle (matches la)" },
            },
        ],
        notes: [
            {
                en: 'The biggest change is the position. English puts the pronoun after the verb. Italian puts it before: "La vedo", not "Vedo la".',
                it: 'Il cambiamento più grande è la posizione. L\'inglese mette il pronome dopo il verbo. L\'italiano lo mette prima: "La vedo", non "Vedo la".',
            },
            {
                en: 'Italian separates direct (lo, la: I see him) from indirect (gli, le: I talk to him). English uses him and her for both.',
                it: "L'italiano separa il diretto (lo, la: lo vedo) dall'indiretto (gli, le: gli parlo). L'inglese usa him e her per entrambi.",
            },
            {
                en: "In the passato prossimo, the participle agrees with lo, la, li, le: 'L'ho mangiata' (la pizza), 'Li ho visti' (i ragazzi).",
                it: "Nel passato prossimo il participio concorda con lo, la, li, le: 'L'ho mangiata' (la pizza), 'Li ho visti' (i ragazzi).",
            },
        ],
    },
    {
        id: 'reflexive',
        level: 'A2',
        title: { en: 'Reflexive verbs', it: 'Verbi riflessivi' },
        summary: {
            en: 'Daily routine verbs, like waking up and getting dressed.',
            it: 'I verbi della routine quotidiana, come svegliarsi e vestirsi.',
        },
        forms: {
            english: [
                {
                    name: 'Ordinary verbs',
                    formula: {
                        en: 'English usually has no special form: I wake up, I get dressed, I have fun. Reflexive pronouns (myself) are rare: I enjoyed myself.',
                        it: 'L\'inglese di solito non ha una forma speciale: I wake up, I get dressed, I have fun. I pronomi riflessivi (myself) sono rari: I enjoyed myself.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Verbo riflessivo',
                    formula: {
                        en: 'The infinitive ends in -si (svegliarsi). Before the verb you add mi, ti, si, ci, vi, si: mi sveglio, ti svegli, si sveglia.',
                        it: 'L\'infinito finisce in -si (svegliarsi). Prima del verbo aggiungi mi, ti, si, ci, vi, si: mi sveglio, ti svegli, si sveglia.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I wake up at seven.', tag: 'wake up' },
                italian: { text: 'Mi sveglio alle sette.', tag: 'Svegliarsi' },
            },
            {
                english: { text: 'My name is Anna.', tag: 'My name is' },
                italian: { text: 'Mi chiamo Anna.', tag: 'Chiamarsi' },
            },
            {
                english: { text: 'She gets dressed.', tag: 'get dressed' },
                italian: { text: 'Si veste.', tag: 'Vestirsi' },
            },
            {
                english: { text: 'We are having fun.', tag: 'have fun' },
                italian: { text: 'Ci divertiamo.', tag: 'Divertirsi' },
            },
            {
                english: { text: 'She woke up late.', tag: 'Past simple' },
                italian: { text: 'Si è svegliata tardi.', tag: 'Passato prossimo with essere' },
            },
        ],
        notes: [
            {
                en: 'Many everyday verbs are reflexive in Italian and not in English: svegliarsi, alzarsi, vestirsi, lavarsi, chiamarsi, divertirsi, sentirsi. Learn them with the -si ending.',
                it: "Molti verbi di ogni giorno sono riflessivi in italiano e non in inglese: svegliarsi, alzarsi, vestirsi, lavarsi, chiamarsi, divertirsi, sentirsi. Impara con la desinenza -si.",
            },
            {
                en: "'Mi chiamo Anna' literally means 'I call myself Anna'. It is the normal way to give your name.",
                it: "'Mi chiamo Anna' significa letteralmente 'I call myself Anna'. È il modo normale per dire il proprio nome.",
            },
            {
                en: "In the passato prossimo, reflexive verbs always use essere, and the participle agrees with the subject: 'Si è svegliata' (she), 'Si sono svegliati' (they).",
                it: "Nel passato prossimo i verbi riflessivi usano sempre essere e il participio concorda con il soggetto: 'Si è svegliata' (lei), 'Si sono svegliati' (loro).",
            },
        ],
    },
    {
        id: 'would',
        level: 'A2',
        title: { en: 'Polite requests and would', it: 'Richieste gentili e condizionale' },
        summary: {
            en: 'Asking politely, giving advice and imagining.',
            it: 'Chiedere con gentilezza, dare consigli e immaginare.',
        },
        forms: {
            english: [
                {
                    name: 'Would / could / should',
                    formula: {
                        en: 'would, could and should + base verb: I would like a coffee. Could you help me? You should study more.',
                        it: 'would, could e should + verbo base: I would like a coffee. Could you help me? You should study more.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Condizionale',
                    formula: {
                        en: 'The same stem as the futuro, plus -ei, -esti, -ebbe, -emmo, -este, -ebbero: lavorerei. The most common ones: vorrei (I would like), potresti (could you), dovresti (you should).',
                        it: 'La stessa radice del futuro, più -ei, -esti, -ebbe, -emmo, -este, -ebbero: lavorerei. I più comuni: vorrei, potresti, dovresti.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I would like a coffee.', tag: 'would like' },
                italian: { text: 'Vorrei un caffè.', tag: 'Condizionale: vorrei' },
            },
            {
                english: { text: 'Could you help me?', tag: 'could' },
                italian: { text: 'Potresti aiutarmi?', tag: 'Condizionale: potresti' },
            },
            {
                english: { text: 'You should study more.', tag: 'should' },
                italian: { text: 'Dovresti studiare di più.', tag: 'Condizionale: dovresti' },
            },
            {
                english: { text: 'I would go to Rome.', tag: 'would + base verb' },
                italian: { text: 'Andrei a Roma.', tag: 'Condizionale: andrei' },
            },
            {
                english: { text: 'Would you like some water?', tag: 'Would you like' },
                italian: { text: "Vorresti un po' d'acqua?", tag: 'Condizionale: vorresti' },
            },
        ],
        notes: [
            {
                en: "Italian uses one tense, the condizionale, for what English does with three words: would, could and should. Vorrei, potrei and dovrei are three of the most useful words in Italian.",
                it: "L'italiano usa un solo tempo, il condizionale, per ciò che l'inglese fa con tre parole: would, could e should. Vorrei, potrei e dovrei sono tre delle parole più utili in italiano.",
            },
            {
                en: "For politeness, avoid the blunt present. 'Voglio un caffè' (I want a coffee) sounds abrupt. 'Vorrei un caffè' is the normal way to order.",
                it: "Per essere gentili, evita il presente diretto. 'Voglio un caffè' suona brusco. 'Vorrei un caffè' è il modo normale per ordinare.",
            },
        ],
    },
    {
        id: 'relative',
        level: 'B1',
        title: { en: 'Relative clauses (who, which, that)', it: 'Frasi relative (che, cui)' },
        summary: {
            en: 'Adding information about a person or thing in the same sentence.',
            it: 'Aggiungere informazioni su una persona o una cosa nella stessa frase.',
        },
        forms: {
            english: [
                {
                    name: 'Who / which / that / whose',
                    formula: {
                        en: 'who for people, which for things, that for both, whose for owners. When the pronoun is the object, it can be left out: the book (that) I read.',
                        it: 'who per le persone, which per le cose, that per entrambi, whose per il possessore. Quando il pronome è il complemento oggetto si può omettere: the book (that) I read.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Che / cui / il cui',
                    formula: {
                        en: 'che for people and things, as subject or object, and it never changes. After a preposition, use cui: l\'amico con cui ho parlato. For "whose", use il / la cui + noun, matching the thing owned.',
                        it: 'che per persone e cose, come soggetto o oggetto, e non cambia mai. Dopo una preposizione si usa cui: l\'amico con cui ho parlato. Per esprimere il possesso (whose) si usa il / la cui + nome, che concorda con la cosa posseduta.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'The man who works here is my father.', tag: 'who' },
                italian: { text: "L'uomo che lavora qui è mio padre.", tag: 'che' },
            },
            {
                english: { text: 'The book I read was good.', tag: 'No pronoun needed' },
                italian: { text: 'Il libro che ho letto era bello.', tag: 'che (required)' },
            },
            {
                english: { text: 'The friend I talked to is here.', tag: 'Preposition at the end' },
                italian: { text: "L'amico con cui ho parlato è qui.", tag: 'Preposition + cui' },
            },
            {
                english: { text: 'The woman whose house is big is my aunt.', tag: 'whose' },
                italian: { text: 'La donna la cui casa è grande è mia zia.', tag: 'la cui' },
            },
            {
                english: { text: 'The city where I live is small.', tag: 'where' },
                italian: { text: 'La città dove vivo è piccola.', tag: 'dove' },
            },
        ],
        notes: [
            {
                en: "English can drop the pronoun: 'the book I read'. Italian never drops che: 'il libro che ho letto'.",
                it: "L'inglese può omettere il pronome: 'the book I read'. L'italiano non omette mai che: 'il libro che ho letto'.",
            },
            {
                en: "In Italian the preposition always comes before cui: 'l'amico con cui ho parlato'. It can never go at the end of the clause, as it can in English ('the friend I talked to').",
                it: "In italiano la preposizione viene sempre prima di cui: 'l'amico con cui ho parlato'. Non può mai andare alla fine della frase, come invece succede in inglese ('the friend I talked to').",
            },
        ],
    },
    {
        id: 'if',
        level: 'B1',
        title: { en: 'If sentences', it: 'Frasi con se' },
        summary: {
            en: 'Real situations, unlikely ones and things that did not happen.',
            it: 'Situazioni reali, improbabili e cose che non sono successe.',
        },
        forms: {
            english: [
                {
                    name: 'If + present / past / past perfect',
                    formula: {
                        en: 'Real: If it rains, I will stay home. Unreal: If I had time, I would travel. Past unreal: If I had known, I would have called.',
                        it: 'Reale: If it rains, I will stay home. Irreale: If I had time, I would travel. Irreale nel passato: If I had known, I would have called.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Se + presente',
                    formula: {
                        en: 'Real situations: se + presente, then presente or futuro: Se piove, resto a casa.',
                        it: 'Situazioni reali: se + presente, poi presente o futuro: Se piove, resto a casa.',
                    },
                },
                {
                    name: 'Se + congiuntivo',
                    formula: {
                        en: 'Unreal: se + congiuntivo imperfetto, then condizionale: Se avessi tempo, viaggerei. Past unreal: congiuntivo trapassato + condizionale passato: Se l\'avessi saputo, avrei chiamato.',
                        it: 'Irreale: se + congiuntivo imperfetto, poi condizionale: Se avessi tempo, viaggerei. Irreale nel passato: congiuntivo trapassato + condizionale passato: Se l\'avessi saputo, avrei chiamato.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'If it rains, I will stay home.', tag: 'If + present, will' },
                italian: { text: 'Se piove, resto a casa.', tag: 'Se + presente, presente' },
            },
            {
                english: { text: 'If I have time, I will call you.', tag: 'If + present, will' },
                italian: { text: 'Se ho tempo, ti chiamerò.', tag: 'Se + presente, futuro' },
            },
            {
                english: { text: 'If I had time, I would travel.', tag: 'If + past, would' },
                italian: { text: 'Se avessi tempo, viaggerei.', tag: 'Se + congiuntivo imperfetto, condizionale' },
            },
            {
                english: { text: 'If I were you, I would study more.', tag: 'If + past, would' },
                italian: { text: 'Se fossi in te, studierei di più.', tag: 'Se + congiuntivo imperfetto, condizionale' },
            },
            {
                english: { text: 'If I had known, I would have called.', tag: 'If + past perfect, would have' },
                italian: { text: "Se l'avessi saputo, avrei chiamato.", tag: 'Se + congiuntivo trapassato, condizionale passato' },
            },
        ],
        notes: [
            {
                en: "After se in an unreal sentence, Italian uses the congiuntivo, never the condizionale. 'Se avrei tempo' is a very common mistake. Say 'Se avessi tempo'.",
                it: "Dopo se in una frase irreale, l'italiano usa il congiuntivo, mai il condizionale. 'Se avrei tempo' è un errore molto comune. Dì 'Se avessi tempo'.",
            },
            {
                en: "English uses a plain past tense for the unreal 'if' (if I had), which looks similar to the Italian congiuntivo imperfetto (se avessi). Both are 'unreal' forms.",
                it: "L'inglese usa un semplice passato per il 'se' irreale (if I had), che assomiglia al congiuntivo imperfetto italiano (se avessi). Entrambe sono forme 'irreali'.",
            },
            {
                en: "In everyday speech many Italians use the imperfetto in both parts for past unreal sentences: 'Se lo sapevo, chiamavo'. It is informal, so use the congiuntivo in writing.",
                it: "Nel parlato di ogni giorno molti italiani usano l'imperfetto in entrambe le parti per l'irreale nel passato: 'Se lo sapevo, chiamavo'. È informale, quindi nello scritto usa il congiuntivo.",
            },
        ],
    },
    {
        id: 'subjunctive',
        level: 'B1',
        title: { en: 'The subjunctive', it: 'Il congiuntivo' },
        summary: {
            en: 'Opinions, wishes, doubts and feelings.',
            it: 'Opinioni, desideri, dubbi e sentimenti.',
        },
        forms: {
            english: [
                {
                    name: 'Mostly ordinary verbs',
                    formula: {
                        en: 'English almost never needs a special form. You use normal verbs: I think she is at home. I want you to come. A true subjunctive appears only in a few fixed cases, like If I were you.',
                        it: 'L\'inglese quasi mai ha bisogno di una forma speciale. Si usano i verbi normali: I think she is at home. I want you to come. Un vero congiuntivo compare solo in pochi casi fissi, come If I were you.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Congiuntivo presente',
                    formula: {
                        en: 'After che, when the main clause expresses opinion, wish, doubt, feeling or necessity. -are verbs: -i, -i, -i, -iamo, -iate, -ino (parli). -ere / -ire verbs: -a, -a, -a, -iamo, -iate, -ano (scriva). Common irregulars: sia, abbia, stia, faccia, venga.',
                        it: 'Dopo che, quando la frase principale esprime opinione, desiderio, dubbio, sentimento o necessità. Verbi in -are: -i, -i, -i, -iamo, -iate, -ino (parli). Verbi in -ere / -ire: -a, -a, -a, -iamo, -iate, -ano (scriva). Irregolari comuni: sia, abbia, stia, faccia, venga.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I think she is at home.', tag: 'Normal verb' },
                italian: { text: 'Penso che sia a casa.', tag: 'che + congiuntivo' },
            },
            {
                english: { text: 'I hope you are well.', tag: 'Normal verb' },
                italian: { text: 'Spero che tu stia bene.', tag: 'che + congiuntivo' },
            },
            {
                english: { text: 'It is important that you come.', tag: 'Normal verb' },
                italian: { text: 'È importante che tu venga.', tag: 'che + congiuntivo' },
            },
            {
                english: { text: 'I want you to come.', tag: 'want + you + to + verb' },
                italian: { text: 'Voglio che tu venga.', tag: 'che + congiuntivo' },
            },
            {
                english: { text: 'Although it is late, I am staying.', tag: 'Normal verb' },
                italian: { text: 'Benché sia tardi, resto.', tag: 'benché + congiuntivo' },
            },
            {
                english: { text: 'I think I am late.', tag: 'Normal verb' },
                italian: { text: 'Penso di essere in ritardo.', tag: 'di + infinitive (same subject)' },
            },
        ],
        notes: [
            {
                en: 'Typical cases: opinions (penso che, credo che), wishes (voglio che, spero che), doubt (dubito che), feelings (sono contento/a che), impersonal phrases (è importante che, bisogna che) and conjunctions like benché and affinché.',
                it: 'Casi tipici: opinioni (penso che, credo che), desideri (voglio che, spero che), dubbio (dubito che), sentimenti (sono contento/a che), espressioni impersonali (è importante che, bisogna che) e congiunzioni come benché e affinché.',
            },
            {
                en: "If both parts of the sentence have the same subject, use di + infinitive, not che + congiuntivo: 'Penso di essere in ritardo', not 'Penso che io sia in ritardo'.",
                it: "Se le due parti della frase hanno lo stesso soggetto, usa di + infinito, non che + congiuntivo: 'Penso di essere in ritardo', non 'Penso che io sia in ritardo'.",
            },
            {
                en: "Certainty uses the normal indicative, not the congiuntivo: 'So che è a casa' (I know she is at home).",
                it: "La certezza usa il normale indicativo, non il congiuntivo: 'So che è a casa'.",
            },
        ],
    },
    {
        id: 'passive',
        level: 'B1',
        title: { en: 'The passive', it: 'La forma passiva' },
        summary: {
            en: 'When the action matters more than who does it.',
            it: "Quando conta più l'azione che chi la compie.",
        },
        forms: {
            english: [
                {
                    name: 'Be + past participle',
                    formula: {
                        en: 'am / is / are / was / were + past participle: The Divine Comedy was written by Dante. English uses the passive a lot, including for general statements: English is spoken here.',
                        it: 'am / is / are / was / were + participio passato: The Divine Comedy was written by Dante. L\'inglese usa molto il passivo, anche per le affermazioni generali: English is spoken here.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Essere / venire + participio',
                    formula: {
                        en: 'essere + participle, which agrees with the subject: La Divina Commedia è stata scritta da Dante. venire + participle for actions that happen regularly: Il negozio viene aperto alle nove.',
                        it: 'essere + participio, che concorda con il soggetto: La Divina Commedia è stata scritta da Dante. venire + participio per azioni che avvengono con regolarità: Il negozio viene aperto alle nove.',
                    },
                },
                {
                    name: 'Si passivante',
                    formula: {
                        en: 'si + verb for general statements. The verb agrees with the thing: Si vende una casa, si vendono case.',
                        it: 'si + verbo per le affermazioni generali. Il verbo concorda con la cosa: Si vende una casa, si vendono case.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'The Divine Comedy was written by Dante.', tag: 'was + participle' },
                italian: { text: 'La Divina Commedia è stata scritta da Dante.', tag: 'essere + participle' },
            },
            {
                english: { text: 'The shop is opened at nine.', tag: 'is + participle' },
                italian: { text: 'Il negozio viene aperto alle nove.', tag: 'venire + participle' },
            },
            {
                english: { text: 'Italian is spoken here.', tag: 'is + participle' },
                italian: { text: 'Qui si parla italiano.', tag: 'Si passivante' },
            },
            {
                english: { text: 'Books are sold here.', tag: 'are + participle' },
                italian: { text: 'Qui si vendono libri.', tag: 'Si passivante (plural)' },
            },
            {
                english: { text: 'The windows were cleaned yesterday.', tag: 'were + participle' },
                italian: { text: 'Le finestre sono state pulite ieri.', tag: 'essere + participle' },
            },
        ],
        notes: [
            {
                en: "English uses the passive far more. Italian often prefers the active ('Dante ha scritto la Divina Commedia') or the si form.",
                it: "L'inglese usa il passivo molto di più. L'italiano preferisce spesso l'attivo ('Dante ha scritto la Divina Commedia') o la forma con si.",
            },
            {
                en: "Venire only works in simple tenses. In compound tenses, use essere: 'è stato aperto', not 'è venuto aperto'.",
                it: "Venire funziona solo nei tempi semplici. Nei tempi composti si usa essere: 'è stato aperto', non 'è venuto aperto'.",
            },
            {
                en: "In the si form, the verb agrees with what is sold or spoken: 'Si vende una casa' (one house), 'Si vendono case' (more than one).",
                it: "Nella forma con si il verbo concorda con ciò che si vende o si parla: 'Si vende una casa' (una), 'Si vendono case' (più di una).",
            },
        ],
    },
    {
        id: 'reported',
        level: 'B1',
        title: { en: 'Reporting what someone said', it: 'Riportare ciò che qualcuno ha detto' },
        summary: {
            en: 'Telling someone else what a person said, asked or ordered.',
            it: 'Raccontare ad altri ciò che una persona ha detto, chiesto o ordinato.',
        },
        forms: {
            english: [
                {
                    name: 'Reported speech',
                    formula: {
                        en: 'The verb often moves back one step: "I am tired" becomes He said he was tired. Questions use if: He asked if I was hungry. Orders use to + verb: He told me to wait.',
                        it: 'Il verbo spesso fa un passo indietro: "I am tired" diventa He said he was tired. Le domande usano if: He asked if I was hungry. Gli ordini usano to + verbo: He told me to wait.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Discorso indiretto',
                    formula: {
                        en: 'Similar shift after che: Ha detto che era stanco. Questions use se: Mi ha chiesto se avevo fame. Orders use di + infinitive: Mi ha detto di aspettare.',
                        it: 'Slittamento simile dopo che: Ha detto che era stanco. Le domande usano se: Mi ha chiesto se avevo fame. Gli ordini usano di + infinito: Mi ha detto di aspettare.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'He said he was tired.', tag: 'said + past' },
                italian: { text: 'Ha detto che era stanco.', tag: 'detto che + imperfetto' },
            },
            {
                english: { text: 'She said that she had eaten.', tag: 'said + past perfect' },
                italian: { text: 'Ha detto che aveva mangiato.', tag: 'detto che + trapassato prossimo' },
            },
            {
                english: { text: 'She said she would come.', tag: 'would' },
                italian: { text: 'Ha detto che sarebbe venuta.', tag: 'condizionale passato' },
            },
            {
                english: { text: 'He asked me if I was hungry.', tag: 'asked + if' },
                italian: { text: 'Mi ha chiesto se avevo fame.', tag: 'chiesto + se' },
            },
            {
                english: { text: 'He told me to wait.', tag: 'told + to + verb' },
                italian: { text: 'Mi ha detto di aspettare.', tag: 'detto + di + infinitive' },
            },
        ],
        notes: [
            {
                en: 'Both languages move the verb back in time, so this is easier than it looks. The English past becomes the imperfetto, and the English past perfect becomes the trapassato prossimo.',
                it: "Entrambe le lingue spostano il verbo indietro nel tempo, quindi è più facile di quanto sembri. Il past inglese diventa imperfetto, e il past perfect inglese diventa trapassato prossimo.",
            },
            {
                en: "English 'would' becomes the condizionale passato in Italian: 'sarebbe venuta', not 'verrebbe'.",
                it: "Il 'would' inglese diventa il condizionale passato in italiano: 'sarebbe venuta', non 'verrebbe'.",
            },
            {
                en: 'For orders and requests, English uses to + verb and Italian uses di + infinitive.',
                it: 'Per ordini e richieste, l\'inglese usa to + verbo e l\'italiano usa di + infinito.',
            },
        ],
    },
];